import { cssFns, useColors, useScreenDetector } from "@hermes/ui";
import { Link as RouterLink, type To } from "react-router-dom";

type Link = {
  text?: string;
  to?: To;
  links?: Link[];
};

const BreadcrumbTextLink = ({
  link,
  hasMeta,
}: {
  link: Link;
  hasMeta?: boolean;
}) => {
  const { principal } = useColors();
  const commonItemProps = hasMeta ? { itemProp: "item" } : {};
  const commonTextProps = hasMeta ? { itemProp: "name" } : {};

  if (link.to) {
    return (
      <RouterLink
        to={link.to}
        {...commonItemProps}
        css={{
          color: principal.ebblue,
          whiteSpace: "nowrap",
          textDecoration: "none",
        }}
      >
        <span {...commonTextProps}>{link.text}</span>
      </RouterLink>
    );
  }

  return (
    <span
      {...commonItemProps}
      css={{ whiteSpace: "nowrap", color: principal.gs4 }}
    >
      <span {...commonTextProps}>{link.text}</span>
    </span>
  );
};

const BreadcrumbSeparator = () => (
  <span
    css={[
      {
        display: "inline-block",
        whiteSpace: "nowrap",
      },
      cssFns.typo({ level: "body-1", weight: "regular" }),
    ]}
  >
    &nbsp;/&nbsp;
  </span>
);

export const Breadcrumbs = ({
  links,
  hasMeta = true,
}: {
  links: Link[];
  hasMeta?: boolean;
}) => {
  const { principal } = useColors();
  const { isMobile } = useScreenDetector();

  const filteredLinks = links.filter(
    (link) => link.text || link.to || link.links?.length,
  );

  const renderLinks = () =>
    filteredLinks.map((link, index) => {
      const isLast = index === filteredLinks.length - 1;

      return (
        <span
          key={index}
          {...(hasMeta
            ? {
                itemProp: "itemListElement",
                itemScope: true,
                itemType: "http://schema.org/ListItem",
              }
            : {})}
          css={cssFns.typo({ level: "body-1", weight: "regular" })}
        >
          <BreadcrumbTextLink link={link} />

          {link.links &&
            link.links.map((subLink, subIndex) => (
              <span
                key={subIndex}
                css={[
                  { color: principal.gs8 },
                  cssFns.typo({ level: "body-1", weight: "regular" }),
                ]}
              >
                {subIndex !== 0 && `, `}
                <BreadcrumbTextLink link={subLink} />
              </span>
            ))}

          {!isLast && <BreadcrumbSeparator />}

          {hasMeta && <meta itemProp="position" content={`${index + 1}`} />}
        </span>
      );
    });

  return (
    <div
      {...(hasMeta && {
        itemScope: true,
        itemType: "http://schema.org/BreadcrumbList",
      })}
      css={[
        cssFns.typo({ level: "body-2", weight: "regular" }),
        {
          color: principal.gs8,
          whiteSpace: isMobile ? "nowrap" : "none",
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          "::-webkit-scrollbar": {
            display: "none",
          },
          a: [
            cssFns.typo({ level: "body-1", weight: "regular" }),
            {
              ":hover": {
                opacity: 0.5,
              },
              lineHeight: "28px",
            },
          ],
          span: {
            lineHeight: "28px",
          },
        },
      ]}
    >
      {renderLinks()}
    </div>
  );
};
